/**
 * Hue Bridges Store
 * Manages Hue bridge connections and metadata
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider, isStandaloneMode, TABLES } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { hueLogger } from '@/lib/logger'
import { hueDiscoveryService } from '@/services/hue/HueDiscoveryService'
import { hueBridgeService } from '@/services/hue/HueBridgeService'
import { hueCredentialService } from '@/services/storage/HueCredentialService'
import { hueBackgroundService } from '@/services/hue/HueBackgroundService'
import { Platform } from '@/lib/platform'
import type { DiscoveredBridge } from '@/services/hue/HueDiscoveryService'

// Re-export DiscoveredBridge for external use
export type { DiscoveredBridge }

export interface HueBridge {
  id: string
  user_id: string
  bridge_id: string
  name: string
  ip_address: string
  is_active: boolean
  last_connected: string | null
  created_at: string
  updated_at: string
}

export interface BridgeWithStatus extends HueBridge {
  isOnline: boolean
  hasCredentials: boolean
  lastSeen: number | null
}

export const useHueBridgesStore = defineStore('hueBridges', () => {
  // State
  const bridges = ref<Map<string, HueBridge>>(new Map())
  const discoveredBridges = ref<Map<string, DiscoveredBridge>>(new Map())
  const bridgeStatuses = ref<Map<string, { isOnline: boolean; lastSeen: number }>>(new Map())
  
  // Loading states
  const isDiscovering = ref(false)
  const isConnecting = ref(false)
  const isLoading = ref(false)
  
  // Error state
  const error = ref<string | null>(null)

  // Getters
  const allBridges = computed(() => Array.from(bridges.value.values()))
  
  const bridgesWithStatus = computed((): BridgeWithStatus[] => {
    return allBridges.value.map(bridge => {
      const status = bridgeStatuses.value.get(bridge.bridge_id)
      return {
        ...bridge,
        isOnline: status?.isOnline ?? false,
        hasCredentials: true, // If it's in DB, we had credentials at some point
        lastSeen: status?.lastSeen ?? null
      }
    })
  })

  const onlineBridges = computed(() => 
    bridgesWithStatus.value.filter(bridge => bridge.isOnline)
  )

  const availableDiscovered = computed(() => 
    Array.from(discoveredBridges.value.values())
      .filter(discovered => !bridges.value.has(discovered.id))
  )

  // Actions
  const setError = (message: string | null) => {
    error.value = message
  }

  const clearError = () => {
    error.value = null
  }

  /**
   * Load bridges from database
   */
  const fetchBridges = async (): Promise<void> => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return
    }

    try {
      isLoading.value = true
      clearError()

      const provider = getDataProvider()
      const data = await provider.query<HueBridge>('hue_bridges', {
        orderBy: [{ column: 'created_at', ascending: false }]
      })

      // Update bridges map
      bridges.value.clear()
      for (const bridge of data || []) {
        bridges.value.set(bridge.bridge_id, bridge)
      }

      // Only test connectivity if not already managed by background service
      if (!hueBackgroundService.isManagingSync) {
        hueLogger.info('🔍 Background service not managing sync - checking connectivity')
        await checkBridgeConnectivity()
      } else {
        hueLogger.info('🔄 Background service already managing sync - skipping connectivity check')
      }

    } catch (err) {
      hueLogger.error('Failed to fetch bridges:', err)
      setError(err instanceof Error ? err.message : 'Failed to load bridges')
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Discover available bridges
   */
  const discoverBridges = async (): Promise<void> => {
    // Bridge discovery requires local network access - only works in Tauri
    if (Platform.isWeb()) {
      setError('Bridge discovery only available in desktop app')
      return
    }

    try {
      isDiscovering.value = true
      clearError()

      const discovered = await hueDiscoveryService.discoverBridges()
      
      discoveredBridges.value.clear()
      for (const bridge of discovered) {
        discoveredBridges.value.set(bridge.id, bridge)
      }

      hueLogger.info(`Discovered ${discovered.length} bridges`)
    } catch (err) {
      hueLogger.error('Bridge discovery failed:', err)
      setError(err instanceof Error ? err.message : 'Bridge discovery failed')
    } finally {
      isDiscovering.value = false
    }
  }

  /**
   * Add a bridge by IP address
   */
  const addBridgeByIP = async (ipAddress: string): Promise<DiscoveredBridge | null> => {
    // Bridge discovery requires local network access - only works in Tauri
    if (Platform.isWeb()) {
      setError('Bridge connection only available in desktop app')
      return null
    }

    try {
      clearError()
      const discovered = await hueDiscoveryService.addBridgeByIP(ipAddress)
      
      if (discovered) {
        discoveredBridges.value.set(discovered.id, discovered)
      }
      
      return discovered
    } catch (err) {
      hueLogger.error('Failed to add bridge by IP:', err)
      setError(err instanceof Error ? err.message : 'Failed to add bridge')
      return null
    }
  }

  /**
   * Connect to a discovered bridge (authentication flow)
   */
  const connectToBridge = async (discovered: DiscoveredBridge): Promise<boolean> => {
    // Bridge connection requires local network access - only works in Tauri
    if (Platform.isWeb()) {
      setError('Bridge connection only available in desktop app')
      return false
    }

    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return false
    }

    try {
      isConnecting.value = true
      clearError()

      // Authenticate with bridge
      const authResult = await hueBridgeService.authenticateBridge(discovered)
      
      if (!authResult.success) {
        if (authResult.needsLinkButton) {
          setError('Press the link button on the bridge and try again')
        } else {
          setError(authResult.error || 'Authentication failed')
        }
        return false
      }

      // Get bridge info (use bridgeid from discovered bridge, or fallback to id)
      const bridgeInfo = await hueBridgeService.getBridgeInfo(discovered.bridgeid || discovered.id, discovered.internalipaddress)
      if (!bridgeInfo) {
        setError('Failed to get bridge information')
        return false
      }

      // Save bridge to database
      const provider = getDataProvider()
      const result = await provider.insert<HueBridge>('hue_bridges', {
        user_id: authStore.user.id,
        bridge_id: bridgeInfo.bridgeid,
        name: bridgeInfo.name,
        ip_address: discovered.internalipaddress,
        is_active: true,
        last_connected: new Date().toISOString()
      })

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to save bridge')
      }

      // Add to local state
      bridges.value.set(bridgeInfo.bridgeid, result.data)
      
      // Remove from discovered list
      discoveredBridges.value.delete(discovered.id)

            // Sync will be handled by background service when needed
      hueLogger.info('Bridge connected successfully:', bridgeInfo.bridgeid)

      // Sync all lights from bridge
      hueLogger.info('Syncing all lights for bridge:', bridgeInfo.bridgeid)
      await syncAllLightsFromBridge(bridgeInfo.bridgeid)

      hueLogger.info(`Successfully connected to bridge ${bridgeInfo.name}`)
      return true

    } catch (err) {
      hueLogger.error('Failed to connect to bridge:', err)
      setError(err instanceof Error ? err.message : 'Failed to connect to bridge')
      return false
    } finally {
      isConnecting.value = false
    }
  }

  /**
   * Remove a bridge connection
   */
  const removeBridge = async (bridgeId: string): Promise<void> => {
    try {
      clearError()
      hueLogger.info(`Removing bridge: ${bridgeId}`)

      // Sync cleanup handled by background service

      // Get the bridge to find its database ID
      const bridge = bridges.value.get(bridgeId)
      if (bridge) {
        const provider = getDataProvider()
        const result = await provider.delete('hue_bridges', bridge.id)
        
        if (!result.success) {
          throw new Error(result.error)
        }
      }

      // Remove credentials
      await hueCredentialService.removeBridgeCredentials(bridgeId)

      // Remove from local state
      bridges.value.delete(bridgeId)
      bridgeStatuses.value.delete(bridgeId)

      hueLogger.info(`Successfully removed bridge ${bridgeId}`)
    } catch (err) {
      hueLogger.error('Failed to remove bridge:', err)
      setError(err instanceof Error ? err.message : 'Failed to remove bridge')
    }
  }

  /**
   * Check connectivity for all bridges
   */
  const checkBridgeConnectivity = async (): Promise<void> => {
    const connectivityChecks = allBridges.value.map(async (bridge) => {
      try {
        const isOnline = await hueBridgeService.testBridgeConnection(bridge.bridge_id, bridge.ip_address)
        bridgeStatuses.value.set(bridge.bridge_id, {
          isOnline,
          lastSeen: isOnline ? Date.now() : (bridgeStatuses.value.get(bridge.bridge_id)?.lastSeen ?? 0)
        })
        
        // Sync is managed by the background service for scene configs
        if (isOnline && bridge.is_active) {
          hueLogger.info(`✅ Bridge ${bridge.bridge_id} is online and will be managed by background service`)
        }
      } catch (error) {
        hueLogger.error(`Connectivity check failed for bridge ${bridge.bridge_id}:`, error)
        bridgeStatuses.value.set(bridge.bridge_id, {
          isOnline: false,
          lastSeen: bridgeStatuses.value.get(bridge.bridge_id)?.lastSeen ?? 0
        })
      }
    })

    await Promise.allSettled(connectivityChecks)
  }

  /**
   * Sync all lights from a bridge
   */
  const syncAllLightsFromBridge = async (bridgeId: string): Promise<void> => {
    try {
      const bridge = bridges.value.get(bridgeId)
      if (!bridge) {
        throw new Error(`Bridge ${bridgeId} not found`)
      }

      hueLogger.info(`🔄 Syncing all lights from bridge ${bridgeId} (${bridge.name})`)
      
      // Use bridge service to discover and sync lights
      const { hueBridgeService } = await import('@/services/hue/HueBridgeService')
      await hueBridgeService.syncAllLightsFromBridge(bridgeId, bridge.ip_address)
      
      // Refresh lights from database to update the UI
      const lightsStore = (await import('@/stores/lights')).useLightsStore()
      await lightsStore.fetchLights()
      
      hueLogger.info(`✅ Successfully synced lights from bridge ${bridgeId}`)
    } catch (error) {
      hueLogger.error(`❌ Failed to sync lights from bridge ${bridgeId}:`, error)
      throw error
    }
  }

  /**
   * Update bridge IP address
   */
  const updateBridgeIP = async (bridgeId: string, newIP: string): Promise<void> => {
    try {
      clearError()
      const bridge = bridges.value.get(bridgeId)
      if (!bridge) {
        throw new Error('Bridge not found')
      }

      const provider = getDataProvider()
      const result = await provider.update('hue_bridges', bridge.id, {
        ip_address: newIP,
        updated_at: new Date().toISOString()
      })

      if (!result.success) {
        throw new Error(result.error)
      }

      // Update local state
      bridge.ip_address = newIP
      bridge.updated_at = new Date().toISOString()
      bridges.value.set(bridgeId, bridge)

      hueLogger.info(`Bridge ${bridgeId} IP updated to ${newIP}`)

      // Re-check connectivity with new IP
      await checkBridgeConnectivity()
    } catch (err) {
      hueLogger.error('Failed to update bridge IP:', err)
      setError(err instanceof Error ? err.message : 'Failed to update bridge IP')
    }
  }

  /**
   * Update bridge activity status
   */
  const updateBridgeStatus = async (bridgeId: string, isActive: boolean): Promise<void> => {
    try {
      const bridge = bridges.value.get(bridgeId)
      if (!bridge) {
        throw new Error('Bridge not found')
      }

      const provider = getDataProvider()
      const result = await provider.update('hue_bridges', bridge.id, { 
        is_active: isActive,
        updated_at: new Date().toISOString()
      })

      if (!result.success) {
        throw new Error(result.error)
      }

      // Update local state
      bridge.is_active = isActive
      bridge.updated_at = new Date().toISOString()
      bridges.value.set(bridgeId, bridge)

      // Bridge sync is now managed by background service through scene configs
      hueLogger.info(`Bridge ${bridgeId} ${isActive ? 'activated' : 'deactivated'} - managed by background service`)
    } catch (err) {
      hueLogger.error('Failed to update bridge status:', err)
      setError(err instanceof Error ? err.message : 'Failed to update bridge status')
    }
  }

  /**
   * Get a specific bridge
   */
  const getBridge = (bridgeId: string): HueBridge | undefined => {
    return bridges.value.get(bridgeId)
  }

  /**
   * Get current sync leader for a bridge
   * Note: In standalone mode, this always returns null as multi-device sync is cloud-only
   */
  const getActiveLeader = async (bridgeId: string) => {
    // Multi-device sync is a cloud-only feature
    if (isStandaloneMode()) {
      return null
    }
    
    const authStore = useAuthStore()
    if (!authStore.user) return null

    try {
      const provider = getDataProvider()
      const sessions = await provider.query(TABLES.HUE_SYNC_SESSIONS, {
        filters: [
          { column: 'bridge_id', operator: 'eq', value: bridgeId },
          { column: 'is_leader', operator: 'eq', value: true }
        ],
        limit: 1
      })

      return sessions.length > 0 ? sessions[0] : null
    } catch (err) {
      hueLogger.error('Error getting active leader:', err)
      return null
    }
  }

  /**
   * Set current device/tab as the sync leader
   */
  const setAsLeader = async (bridgeId: string): Promise<boolean> => {
    try {
      const { hueSyncCoordinator } = await import('@/services/hue/HueSyncCoordinator')
      
      // Ensure the coordinator is started for this bridge before setting as leader
      if (!hueSyncCoordinator.isStarted()) {
        const { getDeviceName } = await import('@/lib/deviceInfo')
        const deviceName = getDeviceName()
        await hueSyncCoordinator.start(bridgeId, deviceName)
      }
      
      const success = await hueSyncCoordinator.setAsLeader()
      
      if (success) {
        hueLogger.success(`✅ Set as leader for bridge ${bridgeId}`)
      }
      
      return success
    } catch (err) {
      hueLogger.error('Failed to set as leader:', err)
      setError(err instanceof Error ? err.message : 'Failed to set as leader')
      return false
    }
  }

  return {
    // State
    bridges: computed(() => bridges.value),
    discoveredBridges: computed(() => discoveredBridges.value),
    bridgeStatuses: computed(() => bridgeStatuses.value),
    isDiscovering: computed(() => isDiscovering.value),
    isConnecting: computed(() => isConnecting.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),

    // Getters
    allBridges,
    bridgesWithStatus,
    onlineBridges,
    availableDiscovered,

    // Actions
    fetchBridges,
    discoverBridges,
    addBridgeByIP,
    connectToBridge,
    removeBridge,
    checkBridgeConnectivity,
    syncAllLightsFromBridge,
    updateBridgeIP,
    updateBridgeStatus,
    getBridge,
    getActiveLeader,
    setAsLeader,
    setError,
    clearError
  }
})